import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import axios from "../../axios";
import { ResetApiState } from "../../redux.type";
import {
  OfechoReport,
  GeneralReport,
  ReportStateTypes,
  SemeritReport,
  DailyTicketReport,
  DurationSummaryReport,
  ErcaGeneralDifference,
  TripSubRouteReport,
} from "./Report.util";

const extraActions = createExtraActions();

const name = "report";

const ReportInitialState: ReportStateTypes = {
  ofechoReport: ResetApiState(null),
  resetOfechoReport: ResetApiState(null),

  generalReport: ResetApiState(null),
  resetGeneralReport: ResetApiState(null),

  allGeneralReport: ResetApiState(null),
  resetAllGeneralReport: ResetApiState(null),

  semeritReport: ResetApiState([]),

  dailyTicketReport: ResetApiState([]),
  durationTicketReport: ResetApiState([]),
  durationTicketReportByIssueDate: ResetApiState([]),
  durationSummaryReport: ResetApiState(null),

  ercaGeneralDifference: ResetApiState(null),
  resetERCAGeneralDifference: ResetApiState(false),

  tripSubRouteReport: ResetApiState([]),
  resetTripSubRouteReport: ResetApiState(false),

  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const getTripSubRouteReport = createAsyncThunk<
    TripSubRouteReport[],
    { start_date: string; end_date: string }
  >(`/get-TripSubRouteReport`, async ({ start_date, end_date }, thunkApi) =>
    axios
      .get(
        `/trips/get-trip-sub-route-report?startDate=${start_date}&endDate=${end_date}`
      )
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetSubRouteReport = createAction(`/reset-TripSubRouteReport`);

  const getOfechoReport = createAsyncThunk<OfechoReport, any>(
    `/get-OfechoReport`,
    async (ticket_id: any, thunkApi) =>
      axios
        .get(`/tickets/ofecho/${ticket_id}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetOfechoReport = createAction(`/reset-OfechoReport`);

  const getERCAGeneralDifference = createAsyncThunk<
    ErcaGeneralDifference,
    { start_date: string; end_date: string }
  >(`/get-ERCAGeneralDifference`, async ({ start_date, end_date }, thunkApi) =>
    axios
      .get(
        `/tickets/erca-general-report/difference?startDate=${start_date}&endDate=${end_date}`
      )
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetERCAGeneralDifference = createAction(
    `/reset-ERCAGeneralDifference`
  );

  const getGeneralReport = createAsyncThunk<GeneralReport[], any>(
    `/get-GeneralReport`,
    async ({ route_id, start_date, end_date }, thunkApi) =>
      axios
        .get(`/tickets/general/${route_id}/${start_date}/${end_date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetGeneralReport = createAction(`/reset-GeneralReport`);

  const getAllGeneralReport = createAsyncThunk<
    { route: { from: string; to: string }; reports: GeneralReport[] }[],
    any
  >(`/get-AllGeneralReport`, async ({ start_date, end_date }, thunkApi) =>
    axios
      .get(`/tickets/general/all/${start_date}/${end_date}`)
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetAllGeneralReport = createAction(`/reset-AllGeneralReport`);

  const getSemeritReport = createAsyncThunk<SemeritReport[], any>(
    `/get-semerit-report`,
    async ({ date }, thunkApi) =>
      axios
        .get(`trips/semerit/${date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetGetSemeritReport = createAction(`/reset-semerit-report`);

  const getDailyTicketReport = createAsyncThunk<DailyTicketReport, any>(
    `/get-daily-ticket-report`,
    async ({ date }, thunkApi) =>
      axios
        .get(`tickets/daily-report/date/${date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );
  const resetGetDailyTicketReport = createAction(`/reset-daily-ticket-report`);

  const getDurationTicketReport = createAsyncThunk<DailyTicketReport, any>(
    `/get-duration-ticket-report`,
    async ({ start_date, end_date }, thunkApi) =>
      axios
        .get(`tickets/duration-report/${start_date}/${end_date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );
  const resetGetDurationTicketReport = createAction(
    `/reset-duration-ticket-report`
  );

  const getDurationTicketReportByIssueDate = createAsyncThunk<
    DailyTicketReport,
    any
  >(
    `/get-duration-ticket-report-by-issue-date`,
    async ({ start_date, end_date }, thunkApi) =>
      axios
        .get(`tickets/duration-report-by-issue-date/${start_date}/${end_date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );
  const resetGetDurationTicketReportByIssueDate = createAction(
    `/reset-duration-ticket-report-by-issue-date`
  );

  const getDurationSummaryReport = createAsyncThunk<DurationSummaryReport, any>(
    `/get-duration-summary-report`,
    async ({ start_date, end_date }, thunkApi) =>
      axios
        .get(`tickets/duration-summary-report/${start_date}/${end_date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetGetDurationSummaryReport = createAction(
    `/reset-get-duration-summary-report`
  );

  const resetStates = createAction(`/reset-states`);

  return {
    getTripSubRouteReport,
    resetSubRouteReport,

    getOfechoReport,
    resetOfechoReport,

    getGeneralReport,
    resetGeneralReport,

    getAllGeneralReport,
    resetAllGeneralReport,

    getSemeritReport,
    resetGetSemeritReport,

    getDailyTicketReport,
    resetGetDailyTicketReport,

    getDurationTicketReport,
    resetGetDurationTicketReport,

    getDurationTicketReportByIssueDate,
    resetGetDurationTicketReportByIssueDate,

    getDurationSummaryReport,
    resetGetDurationSummaryReport,

    getERCAGeneralDifference,
    resetERCAGeneralDifference,

    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<ReportStateTypes>
) {
  return {
    ...getOfechoReducer(),
    ...resetGetOfecho(),
    ...getGeneralReportReducer(),
    ...resetGetGeneralReport(),
    ...getAllGeneralReportReducer(),
    ...resetAllGetGeneralReport(),
    ...getSemeritReducer(),
    ...getDailyTicketReducer(),
    ...getDurationTicketReducer(),
    ...getDurationTicketByIssueDateReducer(),
    ...resetDurationTicketByIssueDate(),
    ...getDurationSummaryReportReducer(),
    ...resetDurationSummaryReport(),
    ...getERCAGeneralDifference(),
    ...resetERCAGeneralDifference(),
    ...resetStatesReducer(),
    ...getTripSubRouteReport(),
    ...resetTripSubRouteReport(),
  };

  function getTripSubRouteReport() {
    return {
      ...builder.addCase(
        extraActions.getTripSubRouteReport.pending,
        (state, action) => {
          state.tripSubRouteReport = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTripSubRouteReport.fulfilled,
        (state, action) => {
          state.tripSubRouteReport = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTripSubRouteReport.rejected,
        (state, action) => {
          state.tripSubRouteReport = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetTripSubRouteReport() {
    return {
      ...builder.addCase(extraActions.resetSubRouteReport, (state, action) => {
        state.tripSubRouteReport = ResetApiState([]);
        state.resetTripSubRouteReport = ResetApiState(true);
      }),
    };
  }

  function getERCAGeneralDifference() {
    return {
      ...builder.addCase(
        extraActions.getERCAGeneralDifference.pending,
        (state, action) => {
          state.ercaGeneralDifference = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getERCAGeneralDifference.fulfilled,
        (state, action) => {
          state.ercaGeneralDifference = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getERCAGeneralDifference.rejected,
        (state, action) => {
          state.ercaGeneralDifference = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetERCAGeneralDifference() {
    return {
      ...builder.addCase(
        extraActions.resetERCAGeneralDifference,
        (state, action) => {
          state.ercaGeneralDifference = ResetApiState(null);
          state.resetERCAGeneralDifference = ResetApiState(true);
        }
      ),
    };
  }

  function getOfechoReducer() {
    return {
      ...builder.addCase(
        extraActions.getOfechoReport.pending,
        (state, action) => {
          state.ofechoReport = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getOfechoReport.fulfilled,
        (state, action) => {
          state.ofechoReport = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getOfechoReport.rejected,
        (state, action) => {
          state.ofechoReport = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetGetOfecho() {
    return {
      ...builder.addCase(extraActions.resetOfechoReport, (state, action) => {
        state.resetOfechoReport = ResetApiState(true);
        state.ofechoReport = ResetApiState(null);
      }),
    };
  }

  function getSemeritReducer() {
    return {
      ...builder.addCase(
        extraActions.getSemeritReport.pending,
        (state, action) => {
          state.semeritReport = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getSemeritReport.fulfilled,
        (state, action) => {
          state.semeritReport = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getSemeritReport.rejected,
        (state, action) => {
          state.semeritReport = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function getDailyTicketReducer() {
    return {
      ...builder.addCase(
        extraActions.getDailyTicketReport.pending,
        (state, action) => {
          state.dailyTicketReport = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDailyTicketReport.fulfilled,
        (state, action) => {
          state.dailyTicketReport = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDailyTicketReport.rejected,
        (state, action) => {
          state.dailyTicketReport = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function getDurationTicketReducer() {
    return {
      ...builder.addCase(
        extraActions.getDurationTicketReport.pending,
        (state, action) => {
          state.durationTicketReport = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDurationTicketReport.fulfilled,
        (state, action) => {
          state.durationTicketReport = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDurationTicketReport.rejected,
        (state, action) => {
          state.durationTicketReport = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function getDurationTicketByIssueDateReducer() {
    return {
      ...builder.addCase(
        extraActions.getDurationTicketReportByIssueDate.pending,
        (state, action) => {
          state.durationTicketReportByIssueDate = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDurationTicketReportByIssueDate.fulfilled,
        (state, action) => {
          state.durationTicketReportByIssueDate = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDurationTicketReportByIssueDate.rejected,
        (state, action) => {
          state.durationTicketReportByIssueDate = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDurationTicketByIssueDate() {
    return {
      ...builder.addCase(
        extraActions.resetGetDurationTicketReportByIssueDate,
        (state, action) => {
          state.durationTicketReportByIssueDate = ResetApiState(null);
        }
      ),
    };
  }

  function getGeneralReportReducer() {
    return {
      ...builder.addCase(
        extraActions.getGeneralReport.pending,
        (state, action) => {
          state.generalReport = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getGeneralReport.fulfilled,
        (state, action) => {
          state.generalReport = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getGeneralReport.rejected,
        (state, action) => {
          state.generalReport = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetGetGeneralReport() {
    return {
      ...builder.addCase(extraActions.resetGeneralReport, (state, action) => {
        state.resetGeneralReport = ResetApiState(true);
        state.generalReport = ResetApiState([]);
      }),
    };
  }

  function getAllGeneralReportReducer() {
    return {
      ...builder.addCase(
        extraActions.getAllGeneralReport.pending,
        (state, action) => {
          state.allGeneralReport = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAllGeneralReport.fulfilled,
        (state, action) => {
          state.allGeneralReport = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAllGeneralReport.rejected,
        (state, action) => {
          state.allGeneralReport = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetAllGetGeneralReport() {
    return {
      ...builder.addCase(
        extraActions.resetAllGeneralReport,
        (state, action) => {
          state.resetAllGeneralReport = ResetApiState(true);
          state.allGeneralReport = ResetApiState([]);
        }
      ),
    };
  }

  function getDurationSummaryReportReducer() {
    return {
      ...builder.addCase(
        extraActions.getDurationSummaryReport.pending,
        (state, action) => {
          state.durationSummaryReport = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDurationSummaryReport.fulfilled,
        (state, action) => {
          state.durationSummaryReport = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getDurationSummaryReport.rejected,
        (state, action) => {
          state.durationSummaryReport = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDurationSummaryReport() {
    return {
      ...builder.addCase(
        extraActions.resetGetDurationSummaryReport,
        (state, action) => {
          state.durationSummaryReport = ResetApiState(null);
        }
      ),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.ofechoReport = ResetApiState(null);
        state.resetOfechoReport = ResetApiState(null);
      }),
    };
  }
}

const ReportSlice = createSlice({
  name,
  initialState: ReportInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const ReportActions = {
  ...ReportSlice.actions,
  ...extraActions,
};
export const ReportReducer = ReportSlice.reducer;
